import { useState, useRef } from 'react';
import { Upload, File, X, CheckCircle } from 'lucide-react';
import { filesApi } from '../api/backend';

interface FileUploadProps {
  onFileUploaded: (filePath: string) => void;
  accept?: string;
  maxSize?: number; // em MB
  label?: string;
}

export function FileUpload({ 
  onFileUploaded, 
  accept = '.pdf,.jpg,.jpeg,.png', 
  maxSize = 5,
  label = 'Upload de Arquivo'
}: FileUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validar tamanho
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (selectedFile.size > maxSizeBytes) {
      setError(`Arquivo muito grande. Máximo ${maxSize}MB`);
      return;
    }

    setFile(selectedFile);
    setError(null);
    setUploaded(false);
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);
      setError(null);

      // 1. Obter URL de upload presigned do backend
      const result = await filesApi.getUploadUrl(
        file.name,
        file.type || 'application/pdf')

      const { upload_url, file_path, requiredHeaders } = result;

      // 2. Fazer upload direto para R2 usando URL presigned
      const uploadResponse = await fetch(upload_url, {
        method: 'PUT',
        headers: {
          ...(requiredHeaders || {}),
        },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error('Erro ao fazer upload do arquivo');
      }

      // 3. Sucesso - notificar componente pai
      setUploaded(true);
      onFileUploaded(file_path);
      
    } catch (err: any) {
      console.error('Upload error:', err);
      setError(err.message || 'Erro ao fazer upload');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setFile(null);
    setUploaded(false);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>

      {!file ? (
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-primary-500 dark:hover:border-primary-400 transition-colors">
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            className="hidden"
            id="file-upload"
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer flex flex-col items-center"
          >
            <Upload className="w-12 h-12 text-gray-400 mb-3" />
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Clique para selecionar ou arraste um arquivo
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              Formatos: {accept} • Máximo {maxSize}MB
            </p>
          </label>
        </div>
      ) : (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <File className="w-8 h-8 text-primary-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                  {file.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            <button
              onClick={handleRemove}
              disabled={uploading}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {uploaded ? (
            <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Upload concluído</span>
            </div>
          ) : (
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full py-2 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Enviando...' : 'Fazer Upload'}
            </button>
          )}

          {error && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
